import {Component, Plugins, ElementPreset} from '@packages/systems/wfdl';
import {
  iconName,
  identifier,
  qualify,
  typeName,
} from '@packages/systems/wfdl-Qualified';
// eslint-disable-next-line webflow/no-dangerous-imports
import {wrapAddPanelIcon} from '@packages/systems/ui/shared-ui/wrapAddPanelIcon';
import Block from './Block';
import Blockquote from './Blockquote';
import Emphasized from './Emphasized';
import Figcaption from './Figcaption';
import Figure from './Figure';
import Heading, {HeadingTagType} from './Heading';
import Iframe from './Iframe';
import Image from './Image';
import CodeBlock from './CodeBlock';
import ReverseLabelBoolean from './datatypes/ReverseLabelBoolean';
import {TriStateBoolean} from './datatypes/TriStateBoolean';
import ImageReversedBooleans from './datatypes/ImageReversedBooleans';
import ToggleButton from './datatypes/ToggleButton';
import {LabeledBoolean} from './datatypes/LabeledBoolean';
import {AltText} from './datatypes/AltText';
import {LinkChildren} from './datatypes/LinkChildren';
import {AutoOrNumber} from './datatypes/AutoOrNumber';
import {TagsDropdown} from './datatypes/TagsDropdown';
import {EasingsEnum} from './datatypes/EasingsEnum';
import {
  MSNumber,
  PXNumber,
  MSNumberCartWrapper,
} from './datatypes/NumberWithUnit';
import TimeNumberType from './datatypes/TimeNumberType';
import CodeBlockType from './datatypes/CodeBlockType';
import StyleVariant from './datatypes/StyleVariant';
import {StyleVariantAnyOfList} from './datatypes/StyleVariantAnyOfList';
import VariableMode from './datatypes/VariableMode';

import {numberToText} from './values/numberToText';
import {
  listOfElementsToText,
  listOfElementsIsSet,
  listOfElementsIsNotSet,
} from './values/listOfElementsToText';
import {textToListOfElements} from './values/textToListOfElements';
import {
  linkToText,
  addBasicLinkUtilsToEnv,
  createPageUrlResolver,
  createSectionIdResolver,
} from './values/linkToText';

export {addBasicLinkUtilsToEnv, createPageUrlResolver, createSectionIdResolver};
export type {LinkUrlResolvers} from './values/linkToText';

import Link from './Link';
import List from './List';
import ListItem from './ListItem';
import Paragraph from './Paragraph';
import Span from './Span';
import Strong from './Strong';
import {StringAtom} from './String';
import Superscript from './Superscript';
import Subscript from './Subscript';
import RichText from './RichText';
import InlineCode from './InlineCode';

import {
  AddPanelBlockquote64Icon,
  AddPanelButton64Icon,
  AddPanelCodeBlock64Icon,
  AddPanelDiv64Icon,
  AddPanelHeading64Icon,
  AddPanelImage64Icon,
  AddPanelLink64Icon,
  AddPanelLinkBlock64Icon,
  AddPanelList64Icon,
  AddPanelListItem64Icon,
  AddPanelParagraph64Icon,
  AddPanelRichtext64Icon,
  AddPanelText64Icon,
  AttachmentIcon,
  BlockquoteIcon,
  FieldSwitchIcon as BooleanIcon,
  ElementAddressIcon as AddressIcon,
  ElementArticleIcon as ArticleIcon,
  ElementAsideIcon as AsideIcon,
  ElementButtonIcon as ButtonIcon,
  ElementCodeBlockIcon as CodeBlockIcon,
  ElementDivIcon as DivIcon,
  ElementEmphasisIcon,
  ElementFigureIcon as FigureIcon,
  ElementFooterIcon as FooterIcon,
  ElementHeaderIcon as HeaderIcon,
  ElementLinkIcon,
  ElementSelectIcon as EnumIcon,
  RichMediaIcon as FigureMediaIcon,
  ElementH1Icon as H1Icon,
  ElementH2Icon as H2Icon,
  ElementH3Icon as H3Icon,
  ElementH4Icon as H4Icon,
  ElementH5Icon as H5Icon,
  ElementH6Icon as H6Icon,
  ElementHIcon as HeadingTagPropIcon,
  ElementImageIcon as ImageIcon,
  ElementListDefaultIcon as ListIcon,
  ElementListItemIcon as ListItemIcon,
  ElementMainIcon as MainIcon,
  ElementNavIcon as NavIcon,
  FieldNumberIcon as NumberIcon,
  ElementPIcon as ParagraphIcon,
  HelpOutlineIcon as QuestionMarkIcon,
  ElementRichTextIcon as RichTextIcon,
  ElementSectionIcon as SectionIcon,
  ElementIcon as StarIcon,
  ElementStrongIcon as StrongIcon,
  ElementComponentIcon as SymbolIcon,
  ElementSpanIcon as TextIcon,
  VariableTextSizeIcon as PlainTextIcon,
  ElementEmbedIcon as InlineCodeIcon,
  SuperscriptIcon,
  SubscriptIcon,
  ComponentPropertiesIcon as StyleVariantIcon,
  FilterSmallRotateHueIcon as TriStateBooleanIcon,
  CssPreviewIcon as AttributesIcon,
  VariableIcon as VariableModeIcon,
} from '@packages/systems/spring';

import {
  defaultBlockquoteChildren,
  defaultTextLinkChildren,
  defaultHeadingChildren,
  defaultButtonChildren,
  defaultParagraphChildren,
  defaultTextDivChildren,
  defaultRichTextChildren,
} from '@packages/systems/core/constants/DomNodeDefaults';

import {
  EElement,
  EFunction,
  EText,
  ERecord,
  EBoolean,
  EEnum,
  EList,
  ELiteral,
  TTypeConstructor,
} from '@packages/systems/wfdl-ast';
import {Slider} from './datatypes/Slider';
import {SliderCartWrapper} from './datatypes/SliderCartWrapper';
import {SliderSearchResultsLength} from './datatypes/SliderSearchResultsLength';
import {SliderSearchResultsSnippets} from './datatypes/SliderSearchResultsSnippets';
import {
  DEFAULT_LANGUAGE as CODE_BLOCK_DEFAULT_LANGUAGE,
  DEFAULT_THEME as CODE_BLOCK_DEFAULT_THEME,
} from '@packages/systems/core/constants/CodeBlock';
import {linkIsSet, linkIsNotSet} from './values/linkConditionOperators';
import {operand} from '@packages/systems/plugins/ConditionsOperand';
import {
  styleVariantCondition,
  styleVariantIsAny,
  styleVariantIsNotAny,
} from './values/styleVariant';
import {
  BLOCK_ELEMENT_TAGS,
  BOX_SEMANTIC_TAG_ICON_NAMES,
  type BoxSemanticTag,
  type BoxVariantTag,
} from '@packages/systems/core/constants/BlockElementTags';

/**
 * Wrapped AddPanelIcons to render icons as 64x64.
 */
const BlockQuoteAddPanelIcon = wrapAddPanelIcon(AddPanelBlockquote64Icon);
const ButtonAddPanelIcon = wrapAddPanelIcon(AddPanelButton64Icon);
const CodeBlockAddPanelIcon = wrapAddPanelIcon(AddPanelCodeBlock64Icon);
const DivAddPanelIcon = wrapAddPanelIcon(AddPanelDiv64Icon);
const HeadingAddPanelIcon = wrapAddPanelIcon(AddPanelHeading64Icon);
const ImageAddPanelIcon = wrapAddPanelIcon(AddPanelImage64Icon);
const LinkAddPanelIcon = wrapAddPanelIcon(AddPanelLink64Icon);
const LinkBlockAddPanelIcon = wrapAddPanelIcon(AddPanelLinkBlock64Icon);
const ListAddPanelIcon = wrapAddPanelIcon(AddPanelList64Icon);
const ListItemAddPanelIcon = wrapAddPanelIcon(AddPanelListItem64Icon);
const ParagraphAddPanelIcon = wrapAddPanelIcon(AddPanelParagraph64Icon);
const RichTextAddPanelIcon = wrapAddPanelIcon(AddPanelRichtext64Icon);
const TextAddPanelIcon = wrapAddPanelIcon(AddPanelText64Icon);

const icons = {
  AddPanelBlockquote: BlockQuoteAddPanelIcon,
  AddPanelButton: ButtonAddPanelIcon,
  AddPanelCodeBlock: CodeBlockAddPanelIcon,
  AddPanelDiv: DivAddPanelIcon,
  AddPanelHeading: HeadingAddPanelIcon,
  AddPanelImage: ImageAddPanelIcon,
  AddPanelLink: LinkAddPanelIcon,
  AddPanelLinkBlock: LinkBlockAddPanelIcon,
  AddPanelList: ListAddPanelIcon,
  AddPanelListItem: ListItemAddPanelIcon,
  AddPanelParagraph: ParagraphAddPanelIcon,
  AddPanelRichText: RichTextAddPanelIcon,
  AddPanelText: TextAddPanelIcon,
  // Atom Icons (finder, breadcrumbs, navigator, style, settings, etc...)
  Address: AddressIcon,
  Article: ArticleIcon,
  Aside: AsideIcon,
  Attachment: AttachmentIcon,
  Blockquote: BlockquoteIcon,
  Boolean: BooleanIcon,
  Button: ButtonIcon,
  CodeBlock: CodeBlockIcon,
  Div: DivIcon,
  Emphasized: ElementEmphasisIcon,
  Enum: EnumIcon,
  Figure: FigureIcon,
  FigureMedia: FigureMediaIcon,
  Footer: FooterIcon,
  H1: H1Icon,
  H2: H2Icon,
  H3: H3Icon,
  H4: H4Icon,
  H5: H5Icon,
  H6: H6Icon,
  Header: HeaderIcon,
  HeadingTagProp: HeadingTagPropIcon,
  Image: ImageIcon,
  Link: ElementLinkIcon,
  LinkBlock: ElementLinkIcon,
  List: ListIcon,
  ListItem: ListItemIcon,
  Main: MainIcon,
  Nav: NavIcon,
  Number: NumberIcon,
  Page: DivIcon,
  Paragraph: ParagraphIcon,
  QuestionMark: QuestionMarkIcon,
  RichText: RichTextIcon,
  Section: SectionIcon,
  Text: TextIcon,
  PlainText: PlainTextIcon,
  Star: StarIcon,
  StyleVariant: StyleVariantIcon,
  Strong: StrongIcon,
  Symbol: SymbolIcon,
  Superscript: SuperscriptIcon,
  Subscript: SubscriptIcon,
  InlineCode: InlineCodeIcon,
  TriStateBoolean: TriStateBooleanIcon,
  Attributes: AttributesIcon,
  VariableMode: VariableModeIcon,
} as const;

const basicPlugin = Plugins.name('Basic');
const LineBreak = Component.create({
  displayName: 'Break',
  defaultData: EFunction({
    param: identifier('context'),
    body: ERecord({}),
  }),
  render: EElement({
    // IMPORTANT: For backwards compatibility with symbols, the root element ID
    // of a Component must be the same as the name we use for the Component.
    //
    // This means that where we "bootstrap" our plugins to use this Component
    // we need to use `LineBreak` as the key in the `plugin.components` object.
    //
    // TODO: This is fragile and we should eliminate this requirement.
    // https://app.clickup.com/t/nh3661
    id: 'LineBreak',
    type: qualify(Plugins.name('Builtin'), Component.name('DOM')),
    data: ERecord({
      children: EList([]),
      attributes: EList([]),
      tag: EText('br'),
    }),
  }),
});

const makeHeadingPreset = ({
  tag,
  label,
  category,
  addTabIndex,
}: {
  tag: string;
  label: string;
  category: string;
  addTabIndex?: ElementPreset.ElementPresetAddTabIndex;
}) =>
  ElementPreset.create({
    addTabIndex,
    icon: {
      medium: qualify(basicPlugin, iconName('AddPanelHeading')),
      small: qualify(basicPlugin, iconName(tag.toUpperCase())),
    },
    label,
    factory: EElement({
      id: 'Heading',
      type: qualify(Plugins.name('Basic'), Component.name('Heading')),
      data: ERecord({
        tag: EEnum(tag),
        children: EList(defaultHeadingChildren),
      }),
    }),
    helpData: {
      tag: 'h1...h6',
      description: [
        'Headings organize content and guide readers through your site. Nest headings in order from H1 to H6 (e.g. H3 under an H2).',
      ],
      tip: 'Search engines use headings to pull out important themes and topics from your website content. So, if your site is about dog treats, “dog treats” should be an H1.',
      videoSrc:
        'https://d3e54v103j8qbb.cloudfront.net/videos/tooltips/d2/heading.ce6203c3f8.mp4',
    },
    category,
  });

const makeBoxVariantPreset = (tag: BoxVariantTag) =>
  ElementPreset.create({
    category: 'QuickFindOnly',
    factory: EElement({
      id: 'Box',
      type: qualify(Plugins.name('Basic'), Component.name('Block')),
      data: ERecord({
        tag: EEnum(tag),
        text: EBoolean(false),
        children: EList([]),
      }),
    }),
    icon: {
      medium: qualify(basicPlugin, iconName('AddPanelDiv')),
      small: qualify(
        basicPlugin,
        iconName(BOX_SEMANTIC_TAG_ICON_NAMES[tag as BoxSemanticTag] ?? 'Div')
      ),
    },
    label: `Box <${tag}>`,
    searchTerms: [tag],
    helpData: {
      tag,
      description: [
        'A Box is a structural must-have. Style each box with things like color, text, and images to help bring your website to life.',
      ],
      videoSrc:
        'https://d3e54v103j8qbb.cloudfront.net/videos/tooltips/d2/div-block.82d8e5b214.mp4',
    },
  });

export const defaultTextLink = {
  id: 'Text Link',
  type: qualify(Plugins.name('Basic'), Component.name('Link')),
  data: ERecord({
    button: EBoolean(false),
    block: EText(''),
    eventIds: EList([]),
    children: EList(defaultTextLinkChildren),
    link: ELiteral({
      name: qualify(Plugins.name('Basic'), typeName('Link')),
      value: {mode: 'external', url: '#'},
    }),
  }),
};

export const plugin = {
  components: {
    LineBreak,
  },

  atoms: {
    Block,
    Blockquote,
    CodeBlock,
    Emphasized,
    Figcaption,
    Figure,
    Heading,
    Iframe,
    Image,
    Link,
    List,
    ListItem,
    Paragraph,
    RichText,
    Span,
    String: StringAtom,
    Strong,
    Superscript,
    Subscript,
    InlineCode,
  },
  dataTypes: {
    LinkChildren,
    AltText,
    AutoOrNumber,
    ToggleButton,
    MSNumber,
    PXNumber,
    MSNumberCartWrapper,
    TimeNumber: TimeNumberType,
    ReverseLabelBoolean,
    ImageReversedBooleans,
    LabeledBoolean,
    TagsDropdown,
    EasingsEnum,
    Slider,
    SliderCartWrapper,
    SliderSearchResultsSnippets,
    SliderSearchResultsLength,
    CodeBlock: CodeBlockType,
    StyleVariant,
    HeadingTag: HeadingTagType,
    TriStateBoolean,
    StyleVariantAnyOfList,
    VariableMode,
  },
  values: {
    numberToText,
    listOfElementsToText,
    listOfElementsIsSet,
    listOfElementsIsNotSet,
    textToListOfElements,

    linkCondition: operand(
      TTypeConstructor(qualify(Plugins.name('Basic'), typeName('Link')))
    ),
    linkIsSet,
    linkIsNotSet,
    linkToText,
    styleVariantIsAny,
    styleVariantIsNotAny,
    styleVariantCondition,
  },
  icons,
  elementPresets: {
    // Custom -----------------------------------------------------------------
    DOM: ElementPreset.create({
      addTabIndex: 1000,
      category: 'Custom Code',
      factory: EElement({
        id: 'DOM',
        type: qualify(Plugins.name('Builtin'), Component.name('DOM')),
        data: ERecord({
          tag: EText('div'),
          attributes: EList([]),
          children: EList([]),
          text: EBoolean(false),
          slot: EText(''),
        }),
      }),
      icon: {
        medium: qualify(
          Plugins.name('Builtin'),
          iconName('CustomElementAddPanel')
        ),
        small: qualify(Plugins.name('Builtin'), iconName('CustomElement')),
      },
      label: 'Custom Element',
      helpData: {
        tag: '',
        description: [
          'The Custom element allows advanced users to create and nest elements with any tag or attributes, enabling more complex and customized web designs.',
          'When utilized within a collection or a component, it can bind to fields, unlocking more powerful capabilities and enabling a dynamic, data-driven user experience.',
        ],
      },
    }),

    // Basic ------------------------------------------------------------------
    // A Basic::Block with tag=div. Shown in the Add panel Structure category.
    Box: ElementPreset.create({
      addTabIndex: 1,
      category: 'Structure',
      factory: EElement({
        id: 'Box',
        type: qualify(Plugins.name('Basic'), Component.name('Block')),
        data: ERecord({
          tag: EEnum('div'),
          text: EBoolean(false),
          children: EList([]),
        }),
      }),
      icon: {
        medium: qualify(basicPlugin, iconName('AddPanelDiv')),
        small: qualify(basicPlugin, iconName('Div')),
      },
      label: 'Box',
      searchTerms: [
        ...BLOCK_ELEMENT_TAGS,
        'block',
        'container',
        'flex',
        'grid',
      ],
      helpData: {
        tag: 'div',
        description: [
          'A Box is a structural must-have. Style each box with things like color, text, and images to help bring your website to life.',
        ],
        videoSrc:
          'https://d3e54v103j8qbb.cloudfront.net/videos/tooltips/d2/box.a7c9af2967.mp4',
      },
    }),
    // A Basic::Block with tag=section (the Box-era Section). Surfaced only
    // through Quick Find; the legacy Layout::Section preset is hidden instead.
    Section: makeBoxVariantPreset('section'),
    BoxDiv: makeBoxVariantPreset('div'),
    Header: makeBoxVariantPreset('header'),
    Footer: makeBoxVariantPreset('footer'),
    Nav: makeBoxVariantPreset('nav'),
    Main: makeBoxVariantPreset('main'),
    Article: makeBoxVariantPreset('article'),
    Aside: makeBoxVariantPreset('aside'),
    Address: makeBoxVariantPreset('address'),
    Figure: makeBoxVariantPreset('figure'),
    DivBlock: ElementPreset.create({
      addTabIndex: 3,
      category: null,
      factory: EElement({
        id: 'Div Block',
        type: qualify(Plugins.name('Basic'), Component.name('Block')),
        data: ERecord({
          tag: EEnum('div'),
          text: EBoolean(false),
          children: EList([]),
        }),
      }),
      icon: {
        medium: qualify(basicPlugin, iconName('AddPanelDiv')),
        small: qualify(basicPlugin, iconName('Div')),
      },
      label: 'Div Block',
      helpData: {
        tag: 'div',
        description: [
          'A Div block is a structural must-have. Style each block with things like color, text, and images to help bring your website to life.',
        ],
        videoSrc:
          'https://d3e54v103j8qbb.cloudfront.net/videos/tooltips/d2/div-block.82d8e5b214.mp4',
      },
    }),
    List: ElementPreset.create({
      category: 'Other',
      addTabIndex: 1,
      factory: EElement({
        id: 'List',
        type: qualify(Plugins.name('Basic'), Component.name('List')),
        data: ERecord({
          tag: EEnum('ul'),
          list: ERecord({
            type: EText('list'),
            unstyled: EBoolean(false),
          }),
          children: EList([
            EElement({
              id: 'List Item 1',
              type: qualify(Plugins.name('Basic'), Component.name('ListItem')),
              data: ERecord({
                list: ERecord({
                  type: EText('item'),
                }),
                children: EList([]),
                text: EBoolean(false),
              }),
            }),
            EElement({
              id: 'List Item 2',
              type: qualify(Plugins.name('Basic'), Component.name('ListItem')),
              data: ERecord({
                list: ERecord({
                  type: EText('item'),
                }),
                children: EList([]),
                text: EBoolean(false),
              }),
            }),
            EElement({
              id: 'List Item 3',
              type: qualify(Plugins.name('Basic'), Component.name('ListItem')),
              data: ERecord({
                list: ERecord({
                  type: EText('item'),
                }),
                children: EList([]),
                text: EBoolean(false),
              }),
            }),
          ]),
        }),
      }),
      helpData: {
        tag: 'ul/ol',
        description: [
          'Use this tool to group things together, like links in a navigation menu or steps in a recipe.',
        ],
        videoSrc:
          'https://d3e54v103j8qbb.cloudfront.net/videos/tooltips/d2/list.bd11b9ef9c.mp4',
      },
      icon: {
        medium: qualify(basicPlugin, iconName('AddPanelList')),
        small: qualify(basicPlugin, iconName('List')),
      },
      label: 'List',
    }),
    ListItem: ElementPreset.create({
      category: 'Other',
      addTabIndex: 2,
      factory: EElement({
        id: 'List Item',
        type: qualify(Plugins.name('Basic'), Component.name('ListItem')),
        data: ERecord({
          list: ERecord({
            type: EText('item'),
          }),
          children: EList([]),
          text: EBoolean(false),
        }),
      }),
      helpData: {
        tag: 'li',
        description: [
          'This element allows you to add more items, like links and images, to an existing List element.',
        ],
        tip: 'By default, List items stack. To sit them side-by-side, add a Class and change their Display property to inline-block.',
        videoSrc:
          'https://d3e54v103j8qbb.cloudfront.net/videos/tooltips/d2/list-item.3677015de5.mp4',
      },
      icon: {
        medium: qualify(basicPlugin, iconName('AddPanelListItem')),
        small: qualify(basicPlugin, iconName('ListItem')),
      },
      label: 'List Item',
    }),
    LinkBlock: ElementPreset.create({
      category: 'Links',
      addTabIndex: 1,
      icon: {
        medium: qualify(basicPlugin, iconName('AddPanelLinkBlock')),
        small: qualify(basicPlugin, iconName('Link')),
      },
      label: 'Link Block',
      factory: EElement({
        id: 'Link Block',
        type: qualify(Plugins.name('Basic'), Component.name('Link')),
        data: ERecord({
          button: EBoolean(false),
          block: EText('inline'),
          children: EList([]),
          eventIds: EList([]),
          link: ELiteral({
            name: qualify(Plugins.name('Basic'), typeName('Link')),
            value: {mode: 'external', url: '#'},
          }),
        }),
      }),
      helpData: {
        tag: 'a',
        description: [
          'A Link block is a Div block that acts like a link. Site visitors can click on a Link block to go to another section of your site, view a resource, send an email, etc.',
        ],
        videoSrc:
          'https://d3e54v103j8qbb.cloudfront.net/videos/tooltips/d2/link-block.3c32708ab8.mp4',
      },
    }),
    Button: ElementPreset.create({
      category: 'Links',
      addTabIndex: 3,
      icon: {
        medium: qualify(basicPlugin, iconName('AddPanelButton')),
        small: qualify(basicPlugin, iconName('Button')),
      },
      label: 'Button',
      factory: EElement({
        id: 'Button',
        type: qualify(Plugins.name('Basic'), Component.name('Link')),
        data: ERecord({
          button: EBoolean(true),
          block: EText(''),
          search: ERecord({
            exclude: EBoolean(true),
          }),
          eventIds: EList([]),
          children: EList(defaultButtonChildren),
          link: ELiteral({
            name: qualify(Plugins.name('Basic'), typeName('Link')),
            value: {mode: 'external', url: '#'},
          }),
        }),
      }),
      helpData: {
        tag: 'a',
        description: [
          "Tell visitors what to do next with a clear call to action (CTA), like 'visit my portfolio' or 'subscribe’. Change the text, style, and link destination to fit your needs.",
        ],
        videoSrc:
          'https://d3e54v103j8qbb.cloudfront.net/videos/tooltips/d2/button.2ff47d9c73.mp4',
      },
    }),

    // Typography -------------------------------------------------------------
    Heading: makeHeadingPreset({
      tag: 'h1',
      label: 'Heading',
      category: 'Typography',
      addTabIndex: 1,
    }),
    H1: makeHeadingPreset({
      tag: 'h1',
      label: 'H1',
      category: 'QuickFindOnly',
    }),
    H2: makeHeadingPreset({
      tag: 'h2',
      label: 'H2',
      category: 'QuickFindOnly',
    }),
    H3: makeHeadingPreset({
      tag: 'h3',
      label: 'H3',
      category: 'QuickFindOnly',
    }),
    H4: makeHeadingPreset({
      tag: 'h4',
      label: 'H4',
      category: 'QuickFindOnly',
    }),
    H5: makeHeadingPreset({
      tag: 'h5',
      label: 'H5',
      category: 'QuickFindOnly',
    }),
    H6: makeHeadingPreset({
      tag: 'h6',
      label: 'H6',
      category: 'QuickFindOnly',
    }),
    Paragraph: ElementPreset.create({
      addTabIndex: 3,
      icon: {
        medium: qualify(basicPlugin, iconName('AddPanelParagraph')),
        small: qualify(basicPlugin, iconName('Paragraph')),
      },
      label: 'Paragraph',
      factory: EElement({
        id: 'Paragraph',
        type: qualify(Plugins.name('Basic'), Component.name('Paragraph')),
        data: ERecord({
          children: EList(defaultParagraphChildren),
        }),
      }),
      helpData: {
        tag: 'p',
        description: [
          'Paragraph elements are for multiple-sentence content. You can style it to affect all paragraph elements on your site, or just one.',
        ],
        videoSrc:
          'https://d3e54v103j8qbb.cloudfront.net/videos/tooltips/d2/paragraph.788afe6a9b.mp4',
      },
      category: 'Typography',
    }),
    // Text Block is defined before Text Link so it appears first in Quick
    // Add (cmd+E) search results — users typically add a Text Block far more
    // often than a Text Link, and both presets tie on `addTabIndex`, so the
    // sort falls back to insertion order. See PYMT-5694.
    TextBlock: ElementPreset.create({
      addTabIndex: 2,
      factory: EElement({
        id: 'TextBlock',
        type: qualify(Plugins.name('Basic'), Component.name('Block')),
        data: ERecord({
          text: EBoolean(true),
          tag: EEnum('div'),
          children: EList(defaultTextDivChildren),
        }),
      }),
      icon: {
        medium: qualify(basicPlugin, iconName('AddPanelText')),
        small: qualify(basicPlugin, iconName('Text')),
      },
      label: 'Text Block',
      helpData: {
        tag: 'div',
        description: [
          'A Text block is a generic text container, great for any text that isn’t a Heading, a Paragraph, or a Link.',
        ],
        videoSrc:
          'https://d3e54v103j8qbb.cloudfront.net/videos/tooltips/text-block.8658797579.mp4',
      },
      category: 'Typography',
    }),
    TextLink: ElementPreset.create({
      category: 'Links',
      addTabIndex: 2,
      icon: {
        medium: qualify(basicPlugin, iconName('AddPanelLink')),
        small: qualify(basicPlugin, iconName('Link')),
      },
      label: 'Text Link',
      factory: EElement(defaultTextLink),
      helpData: {
        tag: 'a',
        description: [
          'Use a text link to direct visitors to send an email, go to another page on your site, or follow your dog’s Instagram page.',
          'Style your Text links in the Style panel, and set link settings — like “open in a new tab” — in Settings.',
        ],
        videoSrc:
          'https://d3e54v103j8qbb.cloudfront.net/videos/tooltips/text-link.d4c74d7670.mp4',
      },
    }),
    Blockquote: ElementPreset.create({
      icon: {
        medium: qualify(basicPlugin, iconName('AddPanelBlockquote')),
        small: qualify(basicPlugin, iconName('Blockquote')),
      },
      label: 'Block Quote',
      factory: EElement({
        id: 'Block Quote',
        type: qualify(Plugins.name('Basic'), Component.name('Blockquote')),
        data: ERecord({
          children: EList(defaultBlockquoteChildren),
        }),
      }),
      helpData: {
        tag: 'blockquote',
        description: [
          'Display and style a quote, usually from an external source like a book, article, or the family Zoom chat.',
        ],
        videoSrc:
          'https://d3e54v103j8qbb.cloudfront.net/videos/tooltips/block-quote.f545d31f0e.mp4',
      },
      category: 'Typography',
    }),
    RichText: ElementPreset.create({
      factory: EElement({
        id: 'Rich Text',
        type: qualify(Plugins.name('Basic'), Component.name('RichText')),
        data: ERecord({
          rich: EBoolean(true),
          tag: EEnum('div'),
          slot: EText(''),
          children: EList(defaultRichTextChildren),
        }),
      }),
      icon: {
        medium: qualify(basicPlugin, iconName('AddPanelRichText')),
        small: qualify(basicPlugin, iconName('RichText')),
      },
      label: 'Rich Text',
      helpData: {
        description: [
          'Create and format various text types, images, and video all in one, instead of individually.',
          'Rich text is best for long-form content, like blog posts, product descriptions, or a personal essay about your favorite snack.',
        ],
        videoSrc:
          'https://d3e54v103j8qbb.cloudfront.net/videos/tooltips/d2/rich-text.faeed9d59c.mp4',
      },
      category: 'Typography',
    }),
    CodeBlock: ElementPreset.create({
      icon: {
        medium: qualify(basicPlugin, iconName('AddPanelCodeBlock')),
        small: qualify(basicPlugin, iconName('CodeBlock')),
      },
      label: 'Code Block',
      factory: EElement({
        id: 'Code Block',
        type: qualify(Plugins.name('Basic'), Component.name('CodeBlock')),
        data: ERecord({
          code: EText(''),
          language: EEnum(CODE_BLOCK_DEFAULT_LANGUAGE),
          theme: EEnum(CODE_BLOCK_DEFAULT_THEME),
          lineNumbers: EBoolean(false),
        }),
      }),
      helpData: {
        description: [
          'Use a Code Block to display code snippets on your site. Syntax will be highlighted based on the selected coding language and color theme.',
        ],
        videoSrc:
          'https://d3e54v103j8qbb.cloudfront.net/videos/tooltips/d2/codeblock.93152b3ff2.mp4',
      },
      category: 'Other',
      addTabIndex: 10,
    }),

    // Media  -----------------------------------------------------------------
    Image: ElementPreset.create({
      factory: ({getAsset}) => {
        if (getAsset === undefined) {
          throw Error(
            'getAsset is required to instantiate Image elementPreset'
          );
        }
        const {url, id} = getAsset('Basic', 'placeholder.svg');

        return EElement({
          id: 'Image',
          type: qualify(Plugins.name('Basic'), Component.name('Image')),
          data: ERecord({
            attr: ERecord({
              src: EText(url),
              loading: EEnum('lazy'),
              width: EText('auto'),
              height: EText('auto'),
              alt: EText('__wf_reserved_inherit'),
            }),
            img: ELiteral({
              name: qualify(Plugins.name('Asset'), typeName('Image')),
              value: {id},
            }),
            temp: ERecord({
              img: ERecord({
                src: EText(''),
              }),
            }),
            srcsetDisabled: EBoolean(false),
            sizes: EList([]),
          }),
        });
      },
      helpData: {
        tag: 'img',
        description: [
          'Use this Element to place images, graphics, and more to a section of your site. Upload bitmap, GIF, PNG, JPEG, SVG, WebP, and AVIF files up to 4 MB.',
        ],
        tip: 'Check the HiDPI checkbox to have Webflow adjust the graphic’s pixel density based on a visitor’s screen resolution.',
        link: {
          text: 'Learn more here.',
          href: 'https://university.webflow.com/lesson/image#edit-image-settings',
        },
        videoSrc:
          'https://d3e54v103j8qbb.cloudfront.net/videos/tooltips/d2/image.ede548c3c4.mp4',
      },
      icon: {
        medium: qualify(basicPlugin, iconName('AddPanelImage')),
        small: qualify(basicPlugin, iconName('Image')),
      },
      label: 'Image',
      category: 'Media',
      addTabIndex: 1,
    }),
  },
} as const;
